import type { IControlSettingUI, IDynamicControlSetting } from '../../type';

import { ORDER_CONTROL_SETTING_UI } from '~/modules/order-analytic/settings';
import { reorderChildInParent } from '../../Control';
import { CONTROL_SETTING_UI } from '../settings';
import { EControlSettingType } from '../../type';
import { create } from 'zustand';

export interface IControlSettingStore {
  controlSettingUI: Record<EControlSettingType, IControlSettingUI[]>;
  setControlSettingUI: (controlSettingUI: IControlSettingUI[], type: EControlSettingType) => void;
  updateSettingUIByPreset: (preset: IDynamicControlSetting, type: EControlSettingType) => void;
  resetControlSettingUI: (type: EControlSettingType) => void;
  deleteControlUI: (id: string, type: EControlSettingType) => void;
  reorderControlChild: (
    data: { parent: IControlSettingUI; uid: string; newIndex: number },
    type: EControlSettingType,
  ) => void;
  addControlSettingUIs: (
    type: EControlSettingType,
    controlId: string,
    newUIs: IControlSettingUI[],
  ) => void;
}

export const useControlSettingStore = create<IControlSettingStore>()((set, get) => ({
  controlSettingUI: {
    [EControlSettingType.REPORT]: CONTROL_SETTING_UI,
    [EControlSettingType.ORDER]: ORDER_CONTROL_SETTING_UI,
  },
  setControlSettingUI: (controlSettingUI, type) =>
    set({ controlSettingUI: { ...get().controlSettingUI, [type]: controlSettingUI } }),

  updateSettingUIByPreset: (preset, type) => {
    const controlSettingUI = get().controlSettingUI;
    const current = controlSettingUI[type];

    const updated = current.map((setting) => {
      const settingPreset = preset[setting.id];
      if (!settingPreset) return setting;
      return {
        ...setting,
        ...settingPreset,
      };
    });

    set({ controlSettingUI: { ...controlSettingUI, [type]: updated } });
  },

  resetControlSettingUI: (type) => {
    const initialUI =
      type === EControlSettingType.ORDER ? ORDER_CONTROL_SETTING_UI : CONTROL_SETTING_UI;
    set({ controlSettingUI: { ...get().controlSettingUI, [type]: initialUI } });
  },

  addControlSettingUIs: (type, controlId, newUIs) => {
    const controlSettingUI = get().controlSettingUI;
    const current = controlSettingUI[type];

    const updated = current.map((setting) => {
      if (setting.id !== controlId || !setting.controls) return setting;
      return {
        ...setting,
        controls: [...setting.controls, ...newUIs],
      };
    });

    set({ controlSettingUI: { ...controlSettingUI, [type]: updated } });
  },

  deleteControlUI: (uid, type) => {
    const controlSettingUI = get().controlSettingUI;
    const current = controlSettingUI[type];

    const deleteRecursively = (controls: IControlSettingUI[]): IControlSettingUI[] => {
      return controls
        .filter((ctrl) => ctrl.uid !== uid)
        .map((ctrl) => ({
          ...ctrl,
          controls: ctrl.controls ? deleteRecursively(ctrl.controls) : ctrl.controls,
        }));
    };

    const updated = deleteRecursively(current);
    set({ controlSettingUI: { ...controlSettingUI, [type]: updated } });
  },

  reorderControlChild: ({ parent, uid, newIndex }, type) => {
    const controlSettingUI = get().controlSettingUI;
    const current = controlSettingUI[type];

    const updated = current.map((setting) => {
      if (setting.id !== parent.id) return setting;
      return reorderChildInParent(setting, uid, newIndex);
    });

    set({ controlSettingUI: { ...controlSettingUI, [type]: updated } });
  },
}));
